import { MouseEvent, forwardRef } from "react";
import { Button, ButtonProps } from "../Button";
import { useDrawerContext } from "./Drawer";

/**
 * Props for the DrawerCancelButton component
 * @extends ButtonProps
 */
export type DrawerCancelButtonProps = ButtonProps;

/**
 * A button component that automatically closes the drawer when clicked.
 *
 * Features:
 * - Automatically closes the drawer when clicked
 * - Extends Button component functionality
 * - Accessible with proper ARIA attributes
 * - Consistent styling with the design system
 * - Supports all standard button props
 * - Proper focus management
 * - Can be used anywhere within the drawer context
 *
 * @example
 * <DrawerCancelButton appearance="ghost" size="small">
 *   Cancel
 * </DrawerCancelButton>
 */
export const DrawerCancelButton = forwardRef<
  HTMLButtonElement,
  DrawerCancelButtonProps
>((props, ref) => {
  const { onClick, ...rest } = props;
  const { close } = useDrawerContext();
  const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    close();
  };
  return (
    <Button
      aria-label="Close drawer"
      data-anv="drawer-cancel-button"
      onClick={onClickHandler}
      ref={ref}
      {...rest}
    />
  );
});

DrawerCancelButton.displayName = "DrawerCancelButton";
